import { Global, css } from '@emotion/react';
import theme from './theme';

export const GlobalStyles = () => (
	<Global
		styles={css`
			/* アニメーションキーフレーム */
			${theme.animations.fadeIn}
			${theme.animations.slideIn}
			${theme.animations.scaleIn}
			${theme.animations.float}
			${theme.animations.gradientShift}

			* {
				margin: 0;
				padding: 0;
				box-sizing: border-box;
			}

			html,
			body,
			#root,
			.App {
				margin: 0;
				padding: 0;
				min-height: 100vh;
			}

			html {
				scroll-behavior: smooth;
			}

			body {
				font-family: ${theme.typography.fontFamily.primary};
				-webkit-font-smoothing: antialiased;
				-moz-osx-font-smoothing: grayscale;
				color: #000000;
				background: #ffffff;
			}

			code {
				font-family: ${theme.typography.fontFamily.mono};
			}

			/* スクロールバーのカスタマイズ */
			::-webkit-scrollbar {
				width: 10px;
				height: 10px;
			}

			::-webkit-scrollbar-track {
				background: ${theme.colors.neutral[100]};
			}

			::-webkit-scrollbar-thumb {
				background: linear-gradient(
					135deg,
					${theme.colors.primary.main},
					${theme.colors.primary.dark}
				);
				border-radius: ${theme.borderRadius.full};
				transition: background ${theme.transitions.base};
			}

			::-webkit-scrollbar-thumb:hover {
				background: linear-gradient(
					135deg,
					${theme.colors.primary.light},
					${theme.colors.primary.main}
				);
			}

			/* テキスト選択のカスタマイズ */
			::selection {
				background: ${theme.colors.primary.main};
				color: white;
			}

			::-moz-selection {
				background: ${theme.colors.primary.main};
				color: white;
			}

			/* リンクのデフォルトスタイル */
			a {
				color: inherit;
				text-decoration: none;
				transition: all ${theme.transitions.base};
			}

			/* フォーカススタイル */
			:focus-visible {
				outline: 2px solid ${theme.colors.primary.main};
				outline-offset: 2px;
				border-radius: ${theme.borderRadius.sm};
			}

			/* ボタンのデフォルトリセット */
			button {
				font-family: inherit;
				cursor: pointer;
				border: none;
				background: none;
			}

			/* 画像の最適化 */
			img {
				max-width: 100%;
				height: auto;
				display: block;
			}

			/* ページコンテンツの基本スタイル */
			.page-content {
				background: transparent;
				position: relative;
				min-height: 100vh;
			}

			/* コンテンツラッパー */
			.content-wrapper {
				max-width: 1200px;
				margin: 0 auto;
				padding: 0 ${theme.spacing[6]};
				position: relative;
				z-index: 10;
			}

			/* タイトルの基本スタイル */
			.title {
				color: ${theme.colors.text.primary};
				font-family: ${theme.typography.fontFamily.secondary};
				font-size: ${theme.typography.fontSize['5xl']};
				font-weight: ${theme.typography.fontWeight.bold};
				line-height: ${theme.typography.lineHeight.tight};
				margin-bottom: ${theme.spacing[4]};
				background: ${theme.colors.primary.gradient};
				-webkit-background-clip: text;
				-webkit-text-fill-color: transparent;
				background-clip: text;
				animation: fadeIn ${theme.transitions.slow} ease-out;
			}

			/* サブタイトルの基本スタイル */
			.subtitle {
				color: ${theme.colors.text.secondary};
				font-size: ${theme.typography.fontSize.lg};
				line-height: ${theme.typography.lineHeight.relaxed};
				margin-bottom: ${theme.spacing[8]};
				animation: fadeIn ${theme.transitions.slow} ease-out 0.2s both;
			}

			/* ふりがなスタイル */
			.hurigana {
				color: ${theme.colors.text.tertiary};
				font-size: ${theme.typography.fontSize.xl};
				font-weight: ${theme.typography.fontWeight.medium};
				margin-bottom: ${theme.spacing[2]};
				animation: fadeIn ${theme.transitions.slow} ease-out 0.1s both;
			}

			/* カードの基本スタイル */
			.card {
				background: white;
				border-radius: ${theme.borderRadius.xl};
				padding: ${theme.spacing[8]};
				box-shadow: ${theme.shadows.lg};
				transition: all ${theme.transitions.base};
				border: 1px solid ${theme.colors.neutral[200]};

				&:hover {
					transform: translateY(-4px);
					box-shadow: ${theme.shadows['2xl']};
					border-color: ${theme.colors.primary.light};
				}
			}

			/* グラスモーフィズムカード */
			.glass-card {
				${theme.glassmorphism.light}
				border-radius: ${theme.borderRadius.xl};
				padding: ${theme.spacing[8]};
				box-shadow: ${theme.shadows.lg};
				transition: all ${theme.transitions.base};

				&:hover {
					transform: translateY(-4px);
					box-shadow: ${theme.shadows['2xl']};
				}
			}

			/* ボタンの基本スタイル */
			.btn {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				padding: ${theme.spacing[3]} ${theme.spacing[6]};
				font-size: ${theme.typography.fontSize.base};
				font-weight: ${theme.typography.fontWeight.semibold};
				border-radius: ${theme.borderRadius.lg};
				transition: all ${theme.transitions.base};
				cursor: pointer;
				border: none;
				text-decoration: none;

				&.btn-primary {
					background: ${theme.colors.primary.gradient};
					color: white;
					box-shadow: ${theme.shadows.md};

					&:hover {
						box-shadow: ${theme.shadows.glowHover};
						transform: translateY(-2px);
					}

					&:active {
						transform: translateY(0);
					}
				}

				&.btn-secondary {
					background: white;
					color: ${theme.colors.primary.main};
					border: 2px solid ${theme.colors.primary.main};

					&:hover {
						background: ${theme.colors.primary.main};
						color: white;
					}
				}
			}

			/* レスポンシブ対応 */
			@media (max-width: ${theme.breakpoints.lg}) {
				.content-wrapper {
					padding: 0 ${theme.spacing[4]};
				}

				.title {
					font-size: ${theme.typography.fontSize['4xl']};
				}
			}

			@media (max-width: ${theme.breakpoints.md}) {
				.title {
					font-size: ${theme.typography.fontSize['3xl']};
				}

				.subtitle {
					font-size: ${theme.typography.fontSize.base};
				}
			}

			@media (max-width: ${theme.breakpoints.sm}) {
				.content-wrapper {
					padding: 0 ${theme.spacing[3]};
				}

				.title {
					font-size: ${theme.typography.fontSize['2xl']};
				}

				.card,
				.glass-card {
					padding: ${theme.spacing[6]};
				}
			}
		`}
	/>
);

export default GlobalStyles;
